// components/wrapper-main.tsx
"use client";

import { isNationalDayWindowActive } from "@/components/auth/national-day-theme";
import { LanguageType } from "@/lib/types";
import { cn } from "@/lib/utils";
import useAppSettings from "@/store/appSettings";
import { usePathname } from "next/navigation";
import React, { ReactNode, useEffect } from "react";
import LanguageSwitcher from "./ui/language-switcher";
import ThemeSwitcher from "./ui/theme-switcher";

// Live feedback, 2026-09-21: the engineer shared the actual Saudi National
// Day brand guideline deck. Every single slide in it uses a plain thin
// grid (squares) as its background texture on a dark green, not a
// diamond/sadu weave -- the geometric sadu patterns there are used as
// foreground decoration (rug borders, poster frames), not a background
// wash. Matching that: a simple grid line pattern, inline SVG data URI so
// it costs no extra network request. z-0 and confined to this file's own
// background layer only -- see the stacking note below for why it can
// never end up over the card.
const GRID_PATTERN_SVG = encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
  <path d="M40 0 L0 0 0 40" fill="none" stroke="#4ADE80" stroke-width="1" />
</svg>
`);

interface MainWrapperProps {
  children: ReactNode;
} 
const MainWrapper: React.FC<MainWrapperProps> = ({ children }) => {
  const pathname = usePathname() || ""; // ✅ Ensure it's always a string
  const { language, setLanguage } = useAppSettings();

  // Determine the language from the URL
  const langParam = pathname.split("/")[1]; // Assumes format: /en-us/... or /ar-sa/...
  const detectedLang: LanguageType =
    langParam === "ar-sa" ? LanguageType.AR : LanguageType.EN; // Default to EN

    useEffect(() => {
      if (detectedLang !== language) {
        setLanguage(detectedLang);
      }
      console.log("Detected Language:", detectedLang);
    }, [detectedLang, language,setLanguage]); // 🔥 Removed setLanguage ✅

  // Saudi National Day theming (time-boxed, see national-day-theme.tsx):
  // only ever active for a few days around 23/09, and only on this page
  // (MainWrapper has exactly one caller, the login page).
  const isNationalDay = isNationalDayWindowActive();

  return (
    <main
      className={cn(
        "relative flex-grow flex flex-col items-center justify-center overflow-hidden",
        isNationalDay
          ? "bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#006E3B] via-[#00522C] to-[#002C18] dark:from-[#00522C] dark:via-[#002C18] dark:to-[#001B10]"
          : "bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-sky-500 to-[#406A8B] dark:bg-gradient-to-b dark:from-[#112B3C] dark:to-[#0A1128]",
      )}
    >
      {/* Live feedback, 2026-09-21 (twice): the flag/palm corner
          illustration wasn't wanted at all (removed); the geometric
          pattern is wanted, but strictly on the open background, never
          over the card. Earlier this showed up over the card because an
          absolutely positioned sibling stacks above normal-flow content
          by default regardless of DOM order -- the fix isn't opacity or
          placement, it's that the content wrapper below now has its own
          z-10 stacking context, so a full-bleed z-0 pattern here is safe:
          it can never render above real content again. */}
      {isNationalDay && (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 opacity-[0.35]"
          style={{
            backgroundImage: `url("data:image/svg+xml,${GRID_PATTERN_SVG}")`,
            backgroundSize: "40px 40px",
          }}
        />
      )}

      {/* Floating Switchers */}
      <div
        className={cn(
          "absolute top-4 z-10 flex items-center gap-4 max-h",
          detectedLang === LanguageType.EN ? "right-4" : "left-4"
        )}
      >
        <LanguageSwitcher classNames="px-2 py-1  bg-gray-100  rounded-md shadow-md dark:border-gray-600"  />
        <ThemeSwitcher
          label={false}
          classNames="px-2 py-1 bg-gray-100  rounded-md shadow-md border dark:border-gray-600"
        />
      </div>

      {/* Render Children with Language Prop */}
      {/* A plain wrapping div (not display:contents) is required here: an
          absolutely positioned sibling (the corner decorations above)
          stacks above normal-flow content by default regardless of DOM
          order, which is exactly why the pattern previously showed up
          over the card. This div's own position+z-index gives the real
          content a stacking context above that. */}
      <div className="relative z-10">
        {React.cloneElement(children as React.ReactElement<any>, {
          language: detectedLang,
        })}
      </div>
    </main>
  );
};

export default MainWrapper;
